import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  limit as firestoreLimit,
  query,
} from 'firebase/firestore';
import { db } from './firebase';
import { refreshEventIndexQuietly } from './eventIndexStore';

function basePath(clubId, seasonId) {
  return `clubs/${clubId}/events/${seasonId}`;
}

// Default sequence duration when a variant doesn't specify one.
// 5 matches RRS 26 standard start sequence (warning → prep → 1 min → start).
export const DEFAULT_SEQUENCE_MINUTES = 5;
export const SEQUENCE_MINUTES_OPTIONS = [3, 5];

export function getSequenceMinutes(variant) {
  const raw = variant?.sequence_minutes;
  if (raw == null) return DEFAULT_SEQUENCE_MINUTES;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_SEQUENCE_MINUTES;
  return n;
}

/**
 * Add `offsetMinutes` to a `"HH:MM"` string and return `"HH:MM"`.
 * Wraps across midnight if needed. Returns the original string unchanged
 * if parsing fails.
 */
export function addMinutesToHHMM(hhmm, offsetMinutes) {
  if (typeof hhmm !== 'string') return hhmm;
  const match = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return hhmm;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const totalMinutes = ((h * 60 + m + Math.round(offsetMinutes)) % 1440 + 1440) % 1440;
  const newH = Math.floor(totalMinutes / 60);
  const newM = totalMinutes % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

/**
 * Build a published-schedule start object from edited fields.
 *
 * The app reads `time` as the START gun (T=0) and fires the countdown on it, so
 * the in-place editor writes `time` directly (no variant/sequence derivation).
 * `classes`/`flags` are already-parsed arrays. When the original start carried a
 * `sequence_minutes` (i.e. it was published from a variant), we keep it and
 * recompute `warning_time = time − sequence` so the warning never goes stale
 * relative to an edited start time. Starts with no sequence are written with
 * just the three canonical fields.
 */
export function buildPublishedStart({ time, classes, flags, sequence_minutes }) {
  const out = {
    classes: Array.isArray(classes) ? classes : [],
    flags: Array.isArray(flags) ? flags : [],
    time: typeof time === 'string' ? time.trim() : '',
  };
  if (sequence_minutes != null) {
    const seq = getSequenceMinutes({ sequence_minutes });
    out.sequence_minutes = seq;
    out.warning_time = addMinutesToHHMM(out.time, -seq);
  }
  return out;
}

/** Load clubs that have at least one event (season). */
export async function loadClubsWithEvents() {
  const clubSnap = await getDocs(collection(db, 'clubs'));
  const clubs = [];
  const checks = [];
  clubSnap.forEach((d) => {
    clubs.push({ id: d.id, name: d.data().name || d.id });
    checks.push(
      getDocs(query(collection(db, 'clubs', d.id, 'events'), firestoreLimit(1)))
        .then((s) => ({ id: d.id, has: !s.empty })),
    );
  });
  const results = await Promise.all(checks);
  const withEvents = new Set(results.filter((r) => r.has).map((r) => r.id));
  return clubs.filter((c) => withEvents.has(c.id)).sort((a, b) => a.name.localeCompare(b.name));
}

/** Load seasons (events) for a club. */
export async function loadSeasons(clubId) {
  const snap = await getDocs(collection(db, 'clubs', clubId, 'events'));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data().name ? { name: d.data().name } : {}) }));
}

/** Load all racetime data for a club/season. */
export async function loadRacetimeData(clubId, seasonId) {
  const base = basePath(clubId, seasonId);
  const eventDocRef = doc(db, base);

  const [variantSnap, rotationSnap, raceDateSnap, eventSnap] = await Promise.all([
    getDocs(collection(db, base, 'racetime_variants')),
    getDoc(doc(db, base, 'rotation', 'default')),
    getDocs(collection(db, base, 'race_dates')),
    getDoc(eventDocRef),
  ]);

  const variants = {};
  variantSnap.forEach((d) => { variants[d.id] = d.data(); });

  const rotation = rotationSnap.exists() ? rotationSnap.data() : null;

  const raceDates = [];
  raceDateSnap.forEach((d) => { raceDates.push({ _id: d.id, ...d.data() }); });

  const eventData = eventSnap.exists() ? eventSnap.data() : {};
  const classes = eventData.classes || [];

  // Per-event authoring mode (ra-1kga): 'templated' (variants + rotation, DRY —
  // DBSC/seasons) vs 'simple' (direct per-date — Cork Week/regattas). Persisted
  // on the event doc so the choice is remembered and signals intent. Default
  // when unset: infer from whether variants already exist.
  const authoringMode = eventData.racetime_authoring_mode === 'simple' || eventData.racetime_authoring_mode === 'templated'
    ? eventData.racetime_authoring_mode
    : (Object.keys(variants).length ? 'templated' : 'simple');

  // Load course cards for the variant picker. Carry {id, name} so the UI can show
  // human-readable names instead of GUIDs (ra-yuuv), and an explicit `source` so
  // the picker can say WHICH bundle a card comes from (ra-29i6i).
  //
  // Three sources, in precedence order active > preview > draft — mirroring
  // deriveBundleVisibility's live > preview > published (bundleVisibility.js):
  //
  //   'active'  — bundle/v{content_version}: what a sailor's app resolves.
  //   'preview' — bundle/v{preview_content_version}: a PUBLISHED, immutable
  //               version pinned as the event's app-preview. Reviewers load it
  //               in the app before it goes live. It used to be invisible here,
  //               so a card that existed only in the preview bundle could not be
  //               put on a schedule — and since the app filters bundle cards by
  //               the schedule's course_card_id, it could not be previewed AT
  //               ALL, not even by a global admin. You could not preview the
  //               thing you were being asked to activate (ra-29i6i).
  //   'draft'   — wip/vNext: the editing scratchpad. In no published bundle, so
  //               no app — preview included — can resolve it. Shown so a new
  //               card doesn't look missing, but never selectable.
  // `null`/`0` on content_version means NOTHING IS ACTIVATED YET, per
  // schemas/committee/v1/published/event-pointer.schema.json — not "v1". The old
  // `|| 1` fabricated an active v1, which was merely wasteful when this function
  // only read one bundle, but is actively wrong now: on a first-activation event
  // (nothing live, v1 published and pinned as preview) it would label v1's cards
  // 'active', collapse previewIsDistinct, and leave BOTH guards silent — exactly
  // the "sailors cannot resolve this card" window this change exists to close,
  // reintroduced for the newest club, which is precisely who is walking
  // draft → preview → activate for the first time (codex, ra-29i6i).
  const activeVersion = eventData.content_version > 0 ? eventData.content_version : null;
  const previewVersion = eventData.preview_content_version ?? null;

  const readBundleCards = async (version) => {
    if (version == null) return [];
    try {
      const snap = await getDoc(doc(db, base, 'bundle', `v${version}`));
      return snap.exists() ? (snap.data().course_cards || []) : [];
    } catch { return []; }
  };

  // A preview pointer AT the active version adds nothing (already live), so it
  // reads as 'active' — same collapse deriveBundleVisibility makes.
  const previewIsDistinct = previewVersion != null && previewVersion !== activeVersion;

  const [activeCards, previewCards] = await Promise.all([
    readBundleCards(activeVersion),
    previewIsDistinct ? readBundleCards(previewVersion) : Promise.resolve([]),
  ]);

  let draftCards = [];
  try {
    const wipSnap = await getDoc(doc(db, base, 'wip', 'vNext'));
    if (wipSnap.exists()) draftCards = wipSnap.data().course_cards || [];
  } catch { /* ignore */ }

  const courseCards = [];
  const seenCards = new Set();
  const collect = (cards, source, version) => {
    for (const cc of cards) {
      if (!cc || cc.id == null || seenCards.has(cc.id)) continue;
      seenCards.add(cc.id);
      courseCards.push({ id: cc.id, name: cc.name || cc.id, source, version });
    }
  };
  collect(activeCards, 'active', activeVersion);
  collect(previewCards, 'preview', previewVersion);
  collect(draftCards, 'draft', null);

  // Series default active-race window (ra-8en2.1.4), authored once per series
  // from the SIs. Published onto every race day unless a date or variant
  // overrides it. No editor yet — the field is carried so a value written by
  // hand (or by a later authoring UI) actually reaches the app.
  const activeWindow = normaliseActiveWindow(eventData.active_window);

  return {
    variants, rotation, raceDates, classes, courseCards, authoringMode, activeWindow,
    // Version numbers the picker and the publish guard name out loud, so an
    // operator reads "preview v19, not active v18" rather than a bare warning.
    bundleVersions: { active: activeVersion, preview: previewIsDistinct ? previewVersion : null },
  };
}

/**
 * Persist the per-event authoring mode (ra-1kga) on the event doc. `mode` is
 * 'templated' or 'simple'. Stored as `racetime_authoring_mode` so the picker
 * choice survives reloads and signals how the event is built.
 */
export async function saveAuthoringMode(clubId, seasonId, mode) {
  await updateDoc(doc(db, basePath(clubId, seasonId)), { racetime_authoring_mode: mode });
}

// ── Variant CRUD ──

export async function saveVariantMeta(clubId, seasonId, vid, data) {
  await updateDoc(doc(db, basePath(clubId, seasonId), 'racetime_variants', vid), data);
}

export async function saveVariantStarts(clubId, seasonId, vid, starts) {
  await updateDoc(doc(db, basePath(clubId, seasonId), 'racetime_variants', vid), { starts });
}


export async function createVariant(clubId, seasonId, vid, data) {
  await setDoc(doc(db, basePath(clubId, seasonId), 'racetime_variants', vid), data);
}

export async function deleteVariant(clubId, seasonId, vid) {
  await deleteDoc(doc(db, basePath(clubId, seasonId), 'racetime_variants', vid));
}

// ── Rotation CRUD ──

export async function saveRotation(clubId, seasonId, rotation) {
  await setDoc(doc(db, basePath(clubId, seasonId), 'rotation', 'default'), rotation);
}

// ── Race date overrides ──

export async function addRaceDate(clubId, seasonId, dateId, data) {
  await setDoc(doc(db, basePath(clubId, seasonId), 'race_dates', dateId), {
    ...data,
    updated_at: serverTimestamp(),
  });
}

export async function deleteRaceDate(clubId, seasonId, dateId) {
  await deleteDoc(doc(db, basePath(clubId, seasonId), 'race_dates', dateId));
}

// ── Active-race window (ra-8en2.1.4) ──

/** Shape version of the active-race window this build writes. */
export const ACTIVE_WINDOW_SV = 1;

/**
 * Normalise an authored active-race window for publication, or return null.
 *
 * Authored windows come from three places (event doc = series default,
 * race_dates/{date} = per-date override, racetime_variants/{vid} = per-location
 * override) and all three are FREE-FORM at the store layer. This is the one
 * gate they pass through, so it is where a half-written window gets rejected
 * instead of published: a point with no basis, or an absolute point with no
 * time, would resolve to nothing in the app anyway — and a backstop the app
 * silently ignores is worse than one that was never claimed.
 *
 * Returns null for "nothing usable here", so callers can `?? seriesDefault`.
 * Contract: schemas/committee/v1/published/race-schedule.schema.json#/$defs/activeWindow
 */
export function normaliseActiveWindow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const target = normaliseWindowPoint(raw.target_finish);
  const expiry = normaliseWindowPoint(raw.expiry);
  if (!target && !expiry) return null;
  // ALWAYS this build's shape version, never the input's. normaliseWindowPoint
  // emits only the fields this build knows, so whatever `raw.sv` claimed, the
  // OUTPUT is a v1 window — carrying a higher sv through would publish a doc
  // that fails its own `const: 1` contract, and the app, seeing a shape it
  // can't trust, would drop the backstop and let the race never expire.
  return {
    sv: ACTIVE_WINDOW_SV,
    ...(target ? { target_finish: target } : {}),
    ...(expiry ? { expiry } : {}),
  };
}

const HHMM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
/** Schema ceiling for day_offset. A backstop more than a week out is a typo. */
const MAX_DAY_OFFSET = 7;
const WINDOW_SOURCES = ['si', 'admin', 'default'];
const WINDOW_ANCHORS = ['first_warning', 'first_start', 'last_start', 'first_finish'];

/** One end of a window; null when it isn't publishable. */
function normaliseWindowPoint(raw) {
  if (!raw || typeof raw !== 'object') return null;
  // Provenance defaults to 'admin': somebody typed this into the committee, so
  // claiming it came from the SIs would be the one wrong answer.
  const source = WINDOW_SOURCES.includes(raw.source) ? raw.source : 'admin';
  const sourceRef = typeof raw.source_ref === 'string' && raw.source_ref
    ? { source_ref: raw.source_ref }
    : {};

  if (raw.basis === 'absolute') {
    if (typeof raw.time !== 'string' || !HHMM.test(raw.time)) return null;
    // Absent means "the race day itself". PRESENT but unusable drops the whole
    // point instead of falling back to 0 — an offshore 02:00 backstop silently
    // losing its +1 day would fire fourteen hours before the fleet started.
    let dayOffset = 0;
    if (raw.day_offset != null) {
      if (!isWholeNumber(raw.day_offset)) return null;
      if (raw.day_offset < 0 || raw.day_offset > MAX_DAY_OFFSET) return null;
      dayOffset = raw.day_offset;
    }
    return { basis: 'absolute', source, ...sourceRef, time: raw.time, day_offset: dayOffset };
  }
  if (raw.basis === 'relative') {
    if (!WINDOW_ANCHORS.includes(raw.anchor)) return null;
    // WHOLE minutes, and a real number — NOT Number(raw.offset_minutes), which
    // turns null / '' / false into a perfectly valid-looking 0 and publishes a
    // zero-minute backstop that expires the race at the anchor instant.
    if (!isWholeNumber(raw.offset_minutes)) return null;
    return {
      basis: 'relative', source, ...sourceRef,
      anchor: raw.anchor, offset_minutes: raw.offset_minutes,
    };
  }
  return null;
}

/** True only for a real, whole JS number. No coercion — see above. */
function isWholeNumber(value) {
  return typeof value === 'number' && Number.isInteger(value);
}

/**
 * Put every location's `active_window` through the same gate as the day's.
 *
 * The direct-save path carries locations through verbatim so an in-place edit
 * doesn't erase fields the editor never showed — which is right, but it means a
 * per-location window written by hand (or left over from a newer build) would
 * reach `race_schedule` without ever passing `normaliseActiveWindow`. The
 * day-level field is gated and the location-level one would not have been.
 *
 * An unusable window is REMOVED rather than nulled: absent means "inherit the
 * day", which is a real and safe answer, whereas null would assert that this
 * location declares nothing.
 */
function normaliseLocationWindows(locations) {
  if (!locations || typeof locations !== 'object') return {};
  const out = {};
  for (const [locId, loc] of Object.entries(locations)) {
    if (!loc || typeof loc !== 'object') {
      out[locId] = loc;
      continue;
    }
    let next = loc;
    if ('active_window' in next) {
      const window = normaliseActiveWindow(next.active_window);
      const { active_window: _dropped, ...rest } = next;
      next = window ? { ...rest, active_window: window } : rest;
    }
    // Same gate for the race-day vessel (ra-53j9). A normaliser only gates if
    // EVERY write path goes through it, and this is the OTHER way a location
    // reaches race_schedule — the direct per-date editor. A blank-named vessel
    // written by hand would publish a start line with no name on it.
    if ('committee_vessel' in next) {
      const vessel = normaliseCommitteeVessel(next.committee_vessel);
      const { committee_vessel: _droppedVessel, ...rest } = next;
      next = vessel ? { ...rest, committee_vessel: vessel } : rest;
    }
    out[locId] = next;
  }
  return out;
}

// ── Race-day overrides (ra-53j9) ──
//
// WHO is running a start, on what channel, on ONE date. The vessel used to be
// baked into the location key + the variant name ('freebird' /
// 'Thursday Red Fleet (Freebird)'), so a one-day swap to 'Spirit of the Irish'
// could only be expressed by editing the series — club-state mutation, not an
// override. These carry the delta per date instead.

/**
 * Normalise an authored committee-vessel identity, or return null.
 *
 * Null is meaningful here and is NOT the same as "leave it alone": it is how
 * the UI erases an override back to the location's standing identity. Callers
 * distinguish the two by whether the KEY is present, never by the value.
 */
export function normaliseCommitteeVessel(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  // An empty name is not a vessel. Publishing one would replace 'FREEBIRD'
  // with a blank lozenge — telling the sailor LESS than before the override.
  if (!name) return null;
  const id = typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : null;
  const note = typeof raw.note === 'string' && raw.note.trim() ? raw.note.trim() : null;
  return { name, ...(id ? { id } : {}), ...(note ? { note } : {}) };
}

/** Trimmed non-empty string, else null. */
function cleanString(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * Normalise one location's per-date override into the delta that will be
 * applied. Returns null when nothing usable was authored.
 *
 * ABSENT KEY means "unchanged" and is preserved as absent — an override doc
 * carries only what differs, so applying it can never blank a field the RO
 * never touched. `committee_vessel: null` is kept (as null) because that is
 * the explicit erase.
 */
export function normaliseLocationOverride(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  if ('committee_vessel' in raw) {
    const vessel = normaliseCommitteeVessel(raw.committee_vessel);
    // A half-written vessel ({name: '  '}) normalises to null, which would
    // silently ERASE a standing override the RO meant to edit. Only an
    // explicit null (or a real erase intent) may reach the doc as null.
    if (vessel) out.committee_vessel = vessel;
    else if (raw.committee_vessel === null) out.committee_vessel = null;
  }
  const vhf = cleanString(raw.vhf);
  if (vhf) out.vhf = vhf;
  const name = cleanString(raw.name);
  if (name) out.name = name;
  const note = cleanString(raw.note);
  if (note) out.note = note;
  return Object.keys(out).length ? out : null;
}

/** Normalise a whole `location_overrides` map; empty object when nothing survives. */
export function normaliseLocationOverrides(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [locId, override] of Object.entries(raw)) {
    if (!locId) continue;
    const clean = normaliseLocationOverride(override);
    if (clean) out[locId] = clean;
  }
  return out;
}

/**
 * Apply per-date location overrides onto a published `locations` map.
 *
 * PURE, and shared by both write paths — the derivation (so a full Publish
 * reproduces the override) and the live patch (so race day doesn't wait for
 * one). If the two applied overrides differently, an RO's live change would
 * mutate the moment somebody else pressed Publish.
 *
 * An override for a location that isn't racing that date is DROPPED, not
 * inserted: a location with no starts would publish an empty fleet, and the
 * app would offer a start line nobody is starting from.
 */
export function applyLocationOverrides(locations, overrides) {
  const clean = normaliseLocationOverrides(overrides);
  if (!locations || typeof locations !== 'object') return {};
  if (!Object.keys(clean).length) return { ...locations };
  const out = {};
  for (const [locId, loc] of Object.entries(locations)) {
    const override = clean[locId];
    if (!override || !loc || typeof loc !== 'object') { out[locId] = loc; continue; }
    // `note` is the committee's audit trail, not part of the app contract.
    const { note: _note, ...published } = override;
    const next = { ...loc, ...published };
    // Explicit null erases the override; absent leaves whatever the location
    // already carried. Neither may publish a `committee_vessel: null` key that
    // the standing location never had.
    if (published.committee_vessel === null) delete next.committee_vessel;
    out[locId] = next;
  }
  return out;
}

// ── Publish ──

/**
 * Pure derivation: resolve variants + rotation + raceDates into the per-date
 * race_schedule docs — `[{ date, day, locations }]`, the exact shape
 * publishSchedule writes and loadPublishedSchedule returns — WITHOUT persisting.
 *
 * Extracted so the committee can preview the DRAFT (unpublished) Templated
 * schedule in the same matrix that normally renders the published result: in
 * edit mode the matrix shows what Publish WOULD produce, before it's live
 * (ra-c5r7). publishSchedule() below is this derivation plus the Firestore
 * writes, so the preview and the real publish can never drift.
 */
export function deriveScheduleDates(variants, rotation, raceDates, options = {}) {
  if (!rotation) return [];
  // Series default active-race window (ra-8en2.1.4), read once from the SIs and
  // held on the event doc. A per-date override wins over it; a variant's window
  // overrides both, but only for its own location.
  const seriesWindow = normaliseActiveWindow(options.activeWindow);
  const dateOverrides = {};
  (raceDates || []).forEach((rd) => { dateOverrides[rd.date || rd._id] = rd; });

  const dateMap = {};
  Object.keys(rotation).forEach((day) => {
    const dayData = rotation[day];
    if (!dayData?.schedule) return;
    dayData.schedule.forEach((block) => {
      (block.dates || []).forEach((date) => {
        const override = dateOverrides[date];
        if (override?.status === 'cancelled' || override?.status === 'abandoned') return;
        const activeVids = override?.override_variants?.length
          ? override.override_variants
          : block.variants || [];
        if (!dateMap[date]) dateMap[date] = [];
        activeVids.forEach((vid) => {
          if (!dateMap[date].includes(vid)) dateMap[date].push(vid);
        });
      });
    });
  });

  const dates = Object.keys(dateMap).sort();
  return dates.map((date) => {
    const vids = dateMap[date];
    const locations = {};
    let dayName = null;

    vids.forEach((vid) => {
      const v = variants[vid];
      if (!v) return;
      dayName = dayName || v.day;
      const locKey = v.location || vid;
      const seqMinutes = getSequenceMinutes(v);
      if (!locations[locKey]) {
        locations[locKey] = {
          name: v.name, vhf: v.vhf || null,
          course_card_id: v.course_card_id || null,
          document_set_id: v.document_set_id || null,
          fleet: v.fleet || null,
          sequence_minutes: seqMinutes,
          starts: [],
        };
      }
      // Checked for EVERY variant, not just the one that created the bucket:
      // several variants can share a location (grouped by v.location), and if
      // the first happened not to declare a window the later one's override
      // would vanish and the location would silently inherit the day.
      // First declaration wins, matching how name/vhf/card are taken.
      //
      // Only set when the variant declares one — an absent key means "inherit
      // the day", which is not the same as "this location has no window" and
      // must not be flattened into a null.
      if (!locations[locKey].active_window) {
        const locWindow = normaliseActiveWindow(v.active_window);
        if (locWindow) locations[locKey].active_window = locWindow;
      }
      (v.starts || []).forEach((s) => {
        const delay = dateOverrides[date]?.delay_minutes || 0;
        // The user-entered `s.time` is the warning signal gun (T−N). Apply
        // any per-date delay, then derive the T=0 race start time by adding
        // the variant's sequence length. The app reads `time` as the start
        // gun, so publishing the derived value is what makes the countdown
        // fire at the correct moment. `warning_time` is preserved for RC
        // reference and any UI that wants to show both.
        const warningRaw = s.time;
        const warningTime = delay > 0 ? addMinutesToHHMM(warningRaw, delay) : warningRaw;
        const startTime = addMinutesToHHMM(warningTime, seqMinutes);
        locations[locKey].starts.push({
          classes: s.classes || (s.class_id ? [s.class_id] : []),
          flags: s.flags || (s.flag ? [s.flag] : []),
          time: startTime,
          warning_time: warningTime,
          sequence_minutes: seqMinutes,
        });
      });
    });

    Object.values(locations).forEach((loc) => {
      loc.starts.sort((a, b) => a.time.localeCompare(b.time));
    });

    const dayWindow =
      normaliseActiveWindow(dateOverrides[date]?.active_window) || seriesWindow;

    // Race-day overrides (ra-53j9) applied HERE, in the derivation, so a full
    // Publish — which regenerates every date from variants + rotation —
    // reproduces the override rather than reverting the vessel to the series
    // default while the fleet is on the water.
    const overridden = applyLocationOverrides(
      locations,
      dateOverrides[date]?.location_overrides,
    );

    return {
      date,
      day: cleanString(dateOverrides[date]?.day) || dayName || 'unknown',
      locations: overridden,
      ...(dayWindow ? { active_window: dayWindow } : {}),
    };
  });
}

export async function publishSchedule(clubId, seasonId, variants, rotation, raceDates, userEmail, options = {}) {
  const base = basePath(clubId, seasonId);
  const dateDocs = deriveScheduleDates(variants, rotation, raceDates, options);
  if (!dateDocs.length) throw new Error('No dates found in rotation schedule.');

  for (const { date, day, locations, active_window: activeWindow } of dateDocs) {
    await setDoc(doc(db, base, 'race_schedule', date), {
      date,
      day,
      locations,
      // Explicitly null (not omitted) so REMOVING the series window actually
      // clears it from an already-published day. setDoc replaces the doc, but
      // being explicit keeps the intent readable and survives a future merge.
      active_window: activeWindow || null,
    });
  }

  // Index from the ACTUAL race_schedule docs, not just this derivation:
  // directly-authored dates (invisible to the variants builder) must survive a
  // variants publish, or they vanish from the app's enumeration (data loss).
  const dates = await syncPublishedIndex(clubId, seasonId, userEmail);

  // The global event index carries each event's race dates, so publishing a
  // schedule makes it stale. Refresh at publish time (ra-8en2.1.6) — best
  // effort: the schedule IS published at this point, and reporting a failure
  // now would be a lie about the operator's actual change.
  await refreshEventIndexQuietly();

  return dates.length;
}

// ── Published schedule (race_schedule/*) — view + in-place edit ──
//
// race_schedule/{date} (+ _index) is what the APP actually reads. It is authored
// EITHER by publishSchedule() above (resolving variants + rotation) OR directly
// (e.g. Cork Week wrote per-date docs straight here to get dates into the app).
// The variant/rotation builder is blind to a directly-authored schedule, so a
// live schedule could be invisible and uneditable in the committee (ra-gbrd.25).
// These read + write race_schedule/* directly so an RO/admin can see what is
// LIVE and tweak (or author) it in place — no variants required.

/**
 * Load the published schedule by LISTING race_schedule/* (not relying on
 * _index), so a directly-authored schedule with no/stale _index still shows up.
 * Returns { dates: [{ date, day, locations }], index } sorted by date ascending.
 */
export async function loadPublishedSchedule(clubId, seasonId) {
  const base = basePath(clubId, seasonId);
  const snap = await getDocs(collection(db, base, 'race_schedule'));
  const dates = [];
  let index = null;
  snap.forEach((d) => {
    if (d.id === '_index') { index = d.data(); return; }
    const data = d.data() || {};
    dates.push({
      date: data.date || d.id,
      day: data.day || '',
      locations: data.locations || {},
      // Carried so an in-place edit can write it back. This projection is what
      // savePublishedScheduleDate round-trips; anything missing here is
      // silently erased the next time an RO saves the date (ra-gbrd.19 class).
      active_window: data.active_window || null,
    });
  });
  dates.sort((a, b) => a.date.localeCompare(b.date));
  return { dates, index };
}

/**
 * Re-derive race_schedule/_index from the actual race_schedule/* docs so the
 * index stays in step after a direct add/delete. Records who last touched it.
 */
async function syncPublishedIndex(clubId, seasonId, userEmail) {
  const base = basePath(clubId, seasonId);
  const snap = await getDocs(collection(db, base, 'race_schedule'));
  const dates = [];
  snap.forEach((d) => { if (d.id !== '_index') dates.push(d.id); });
  dates.sort();
  await setDoc(doc(db, base, 'race_schedule', '_index'), {
    dates,
    published_at: serverTimestamp(),
    published_by: userEmail || null,
  });
  return dates;
}

/**
 * Write one race_schedule/{date} doc directly (in-place edit or fresh authoring)
 * and re-sync the index. `locations` is the full locations map the app reads.
 */
export async function savePublishedScheduleDate(clubId, seasonId, date, { day, locations, active_window: activeWindow }, userEmail) {
  const base = basePath(clubId, seasonId);
  await setDoc(doc(db, base, 'race_schedule', date), {
    date,
    day: day || 'unknown',
    locations: normaliseLocationWindows(locations),
    // setDoc REPLACES the doc, so a caller that didn't carry the window would
    // erase it just by saving a start time. loadPublishedSchedule hands it
    // back for exactly this round-trip (ra-8en2.1.4).
    active_window: normaliseActiveWindow(activeWindow),
  });
  await syncPublishedIndex(clubId, seasonId, userEmail);
  // A direct date save is immediately live to the app, so the global index's
  // race_dates for this event are stale the moment it lands (ra-8en2.1.6).
  await refreshEventIndexQuietly();
}

/**
 * Re-stamp the published index for the Simple (direct) path. Each "Save Date"
 * already writes the live race_schedule/{date} doc and re-syncs the index, so
 * this is an explicit "Publish" action: it re-derives the index from the current
 * docs and refreshes published_at/published_by. Returns the number of published
 * dates. Mirrors the Templated path's Publish so both authoring modes converge on
 * a single, visible Publish step (ra-c5r7).
 */
export async function republishSchedule(clubId, seasonId, userEmail) {
  const dates = await syncPublishedIndex(clubId, seasonId, userEmail);
  await refreshEventIndexQuietly();
  return dates.length;
}

/**
 * Author a RACE-DAY OVERRIDE for one date: who runs each start, on what
 * channel, and (optionally) the day label — live (ra-53j9).
 *
 * WRITE MODEL — decided, not inherited (the ra-36up "should the committee ever
 * write live?" question). Race-day overrides are the one legitimate
 * live-immediate case: the RO learns at 11:00 that Freebird is out and the gun
 * is at 14:10. A publish-gated override would be correct and useless. So this
 * DUAL-WRITES:
 *
 *   1. race_dates/{date}.location_overrides — the durable authoring record.
 *      Merged, so it never clobbers status / delay / active_window. Without
 *      this the next full Publish would regenerate the date from variants and
 *      silently put the fleet back on the wrong boat.
 *   2. race_schedule/{date} — the doc the app actually reads, PATCHED in place.
 *
 * Step 2 patches the PUBLISHED doc; it deliberately does NOT re-derive from
 * variants + rotation. A re-derive on race day would push whatever unpublished
 * draft edits happen to be sitting in the variants editor live to boats on the
 * water — the override would become a surprise publish.
 *
 * Returns { live, date }: `live` is false when the date has no published
 * schedule doc yet, in which case the override is stored and will apply at the
 * next Publish. The caller must SAY SO — "saved" would read as "the fleet can
 * see it".
 */
export async function saveRaceDayOverride(clubId, seasonId, date, { locationOverrides, day } = {}) {
  const base = basePath(clubId, seasonId);
  const overrides = normaliseLocationOverrides(locationOverrides);
  const dayLabel = cleanString(day);
  if (!Object.keys(overrides).length && !dayLabel) {
    throw new Error('Nothing to override — set a vessel, VHF, start name or day.');
  }

  // Durable record first. If the live patch then fails (offline, rules), the
  // override still lands at the next Publish; the reverse order could leave a
  // live change with no authoring record behind it, which the next Publish
  // would quietly undo.
  //
  // mergeFields, NOT merge: a plain merge DEEP-merges maps, so a location the
  // RO removed from the form would live on inside the stored
  // `location_overrides` and reappear at the next Publish. The named fields are
  // replaced wholesale; everything else on the doc (status, delay_minutes,
  // override_variants, active_window) is untouched.
  const fields = ['date', 'updated_at'];
  const payload = { date, updated_at: serverTimestamp() };
  if (Object.keys(overrides).length) {
    payload.location_overrides = overrides;
    fields.push('location_overrides');
  }
  if (dayLabel) {
    payload.day = dayLabel;
    fields.push('day');
  }
  await setDoc(doc(db, base, 'race_dates', date), payload, { mergeFields: fields });

  const scheduleRef = doc(db, base, 'race_schedule', date);
  const snap = await getDoc(scheduleRef);
  if (!snap.exists()) return { live: false, date };

  const current = snap.data() || {};
  await setDoc(scheduleRef, {
    // Spread the CURRENT doc, not a projection of it: anything this build
    // doesn't know about (a field a newer committee writes) survives the
    // patch instead of being erased by a vessel swap.
    ...current,
    date: current.date || date,
    ...(dayLabel ? { day: dayLabel } : {}),
    locations: applyLocationOverrides(current.locations || {}, overrides),
  });

  // The global event index denormalises each event's race dates; a live patch
  // doesn't change the date list, but it does change what the day says.
  await refreshEventIndexQuietly();
  return { live: true, date };
}

/**
 * Clear a date's race-day override, live (ra-53j9).
 *
 * Erasing means "back to the standing series identity", so the vessel key is
 * REMOVED (not nulled) from each published location — that is what "no
 * override" looks like in the published contract. `name` / `vhf` / `day` are
 * restored from [defaults], because the published doc was overwritten by the
 * override and no longer remembers what they were.
 *
 * [defaults] is `{locations: {locId: {name, vhf}}, day}` — the caller derives
 * it for THIS DATE (via deriveScheduleDates), never by scanning variants for a
 * matching location: DBSC has a Saturday AND a Thursday variant on `freebird`,
 * and a first-match scan would restore the Saturday fleet's name and channel
 * into a Thursday schedule.
 */
export async function clearRaceDayOverride(clubId, seasonId, date, defaults = {}) {
  const base = basePath(clubId, seasonId);
  const defaultsByLocation = defaults.locations || {};
  const defaultDay = cleanString(defaults.day);
  // mergeFields so the empty map REPLACES the stored overrides. A plain merge
  // would deep-merge `{}` into the existing map — i.e. change nothing — and the
  // cleared vessel would come straight back at the next Publish.
  //
  // `day: null` is cleared for the same reason: left in place, a day-label
  // override outlives the "back to the series default" the UI just promised
  // and re-applies itself at the next Publish. Null (not '') — the raw schema
  // types it as string|null and the derivation treats both as "not declared".
  await setDoc(
    doc(db, base, 'race_dates', date),
    { date, location_overrides: {}, day: null, updated_at: serverTimestamp() },
    { mergeFields: ['date', 'location_overrides', 'day', 'updated_at'] },
  );

  const scheduleRef = doc(db, base, 'race_schedule', date);
  const snap = await getDoc(scheduleRef);
  if (!snap.exists()) return { live: false, date };

  const current = snap.data() || {};
  const locations = {};
  for (const [locId, loc] of Object.entries(current.locations || {})) {
    const restore = defaultsByLocation[locId] || {};
    const { committee_vessel: _vessel, ...rest } = loc || {};
    locations[locId] = {
      ...rest,
      ...(restore.name != null ? { name: restore.name } : {}),
      ...(restore.vhf !== undefined ? { vhf: restore.vhf } : {}),
    };
  }
  await setDoc(scheduleRef, {
    ...current,
    ...(defaultDay ? { day: defaultDay } : {}),
    locations,
  });
  await refreshEventIndexQuietly();
  return { live: true, date };
}

/** Delete one published race_schedule/{date} doc and re-sync the index. */
export async function deletePublishedScheduleDate(clubId, seasonId, date, userEmail) {
  const base = basePath(clubId, seasonId);
  await deleteDoc(doc(db, base, 'race_schedule', date));
  await syncPublishedIndex(clubId, seasonId, userEmail);
  // Without this the deleted date lingers in the global index and the
  // boat-first home keeps offering a race that is no longer scheduled.
  await refreshEventIndexQuietly();
}
